/* =========================================================
   Pacekind — landing page behaviour
   Two custom elements, no dependencies:
     <pacekind-atc>     add to cart, reusing the theme's cart API
     <pacekind-sticky>  discreet mobile add-to-cart bar
   The theme owns the cart: window.UnmessCart.add() publishes
   'cart:updated' (which updates <cart-count>), and 'cart:open'
   opens <cart-drawer> when the theme is configured for a drawer.
   ========================================================= */
(function () {
  'use strict';

  /* ---------- Add to cart ---------- */
  if (!customElements.get('pacekind-atc')) {
    class PacekindAtc extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.btn = this.querySelector('button[type="submit"]');
        this.error = this.querySelector('.pk-atc__error');
        this.busy = false;
        if (!this.form || !this.btn) return;
        this.form.addEventListener('submit', (event) => {
          // Without the theme's cart API, let the native form POST happen.
          if (!window.UnmessCart) return;
          event.preventDefault();
          this.add();
        });
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = false;
      }

      clearError() {
        if (!this.error) return;
        this.error.hidden = true;
        this.error.textContent = '';
      }

      async add() {
        if (this.busy) return; // guards double clicks and repeated Enter presses
        this.busy = true;
        this.clearError();
        this.btn.setAttribute('aria-disabled', 'true');
        this.btn.setAttribute('aria-busy', 'true');
        this.btn.classList.add('is-loading');

        try {
          await window.UnmessCart.add({ id: Number(this.dataset.variantId), quantity: 1 });
          // No confirmation state on the button: the cart drawer and the cart
          // count are the confirmation, so the label never changes.
          if (window.Unmess && window.Unmess.cartType === 'drawer') {
            document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
          }
        } catch (err) {
          var message = (err && (err.description || err.message)) || this.dataset.errorText;
          this.showError(message || 'Something went wrong. Please try again.');
        } finally {
          this.busy = false;
          this.btn.setAttribute('aria-disabled', 'false');
          this.btn.removeAttribute('aria-busy');
          this.btn.classList.remove('is-loading');
        }
      }
    }
    customElements.define('pacekind-atc', PacekindAtc);
  }

  /* ---------- Mobile sticky bar ---------- */
  if (!customElements.get('pacekind-sticky')) {
    /* The Shopify cookie/consent banner is bottom-anchored too, so the bar stays
       hidden while that banner (or any element the theme flags) is on screen. */
    function consentVisible() {
      var banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
      if (!banner) return false;
      // The banner is position:fixed, so offsetParent is always null — measure it instead.
      var box = banner.getBoundingClientRect();
      var style = window.getComputedStyle(banner);
      return box.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    }

    class PacekindSticky extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.pk-sticky');
        if (!this.bar || !('IntersectionObserver' in window)) return;

        this.pastTrigger = false;
        this.nearEnd = false;

        var trigger = document.querySelector(this.dataset.trigger || '');
        var end = document.querySelector(this.dataset.end || '');

        // A jump (anchor link, restored scroll position) can deliver several
        // records in one callback, so always read the most recent one.
        function latest(entries) { return entries[entries.length - 1]; }

        // Show once the primary (hero) call to action has scrolled off the top.
        if (trigger) {
          new IntersectionObserver((entries) => {
            var entry = latest(entries);
            this.pastTrigger = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.sync();
          }, { threshold: 0 }).observe(trigger);
        } else {
          this.pastTrigger = true;
        }

        // Hide again next to the final call to action, so the two never stack up.
        if (end) {
          new IntersectionObserver((entries) => {
            this.nearEnd = latest(entries).isIntersecting;
            this.sync();
          }, { rootMargin: '0px 0px -25% 0px', threshold: 0 }).observe(end);
        }

        // Re-check when the consent banner is added or dismissed.
        this._mo = new MutationObserver(() => {
          window.clearTimeout(this._consentTimer);
          this._consentTimer = window.setTimeout(() => this.sync(), 120);
        });
        this._mo.observe(document.body, { childList: true, subtree: false, attributes: true });

        this.sync();
      }

      disconnectedCallback() {
        if (this._mo) this._mo.disconnect();
        document.body.classList.remove('pk-has-sticky');
        document.body.style.removeProperty('--pk-sticky-height');
      }

      sync() {
        var show = this.pastTrigger && !this.nearEnd && !consentVisible();
        this.bar.classList.toggle('is-visible', show);
        this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        // Reserve exactly the bar's own height, so it never sits on page content.
        if (show) {
          var height = Math.ceil(this.bar.getBoundingClientRect().height);
          if (height > 0) document.body.style.setProperty('--pk-sticky-height', height + 'px');
        } else {
          document.body.style.removeProperty('--pk-sticky-height');
        }
        document.body.classList.toggle('pk-has-sticky', show);
      }
    }
    customElements.define('pacekind-sticky', PacekindSticky);
  }
})();
